import { ParamConfig } from "../../engine/nodes/utils/params/ParamsConfig";
import { ColorConversion } from "../Color";
import { Color } from "three";
import { Vector2 } from "three";
import { SpotLight } from "three";
import { Group } from "three";
import { VolumetricSpotLight } from "./spotlight/VolumetricSpotLight";
import { isBooleanTrue } from "../Type";
import { CoreSpotLightHelper } from "./spotlight/CoreSpotLightHelper";
export const DEFAULT_SPOT_LIGHT_PARAMS = {
  color: new Color(1, 1, 1),
  intensity: 1,
  angle: 45,
  penumbra: 0.1,
  decay: 0.1,
  distance: 100,
  showHelper: false,
  helperSize: 1,
  castShadow: false,
  shadowAutoUpdate: true,
  shadowUpdateOnNextRender: false,
  shadowRes: new Vector2(1024, 1024),
  shadowBias: 1e-4,
  shadowNear: 0.1,
  shadowFar: 100,
  shadowRadius: 0,
  tvolumetric: false,
  volAttenuation: 5,
  volAnglePower: 10
};
const DEFAULT = DEFAULT_SPOT_LIGHT_PARAMS;
export function SpotLightParamConfig(Base) {
  return class Mixin extends Base {
    constructor() {
      super(...arguments);
      this.light = ParamConfig.FOLDER();
      this.color = ParamConfig.COLOR(DEFAULT.color.toArray(), {
        conversion: ColorConversion.SRGB_TO_LINEAR
      });
      this.intensity = ParamConfig.FLOAT(DEFAULT.intensity, {
        range: [0, 10],
        rangeLocked: [true, false]
      });
      this.angle = ParamConfig.FLOAT(DEFAULT.angle, { range: [0, 180] });
      this.penumbra = ParamConfig.FLOAT(DEFAULT.penumbra);
      this.decay = ParamConfig.FLOAT(DEFAULT.decay, { range: [0, 1] });
      this.distance = ParamConfig.FLOAT(DEFAULT.distance, { range: [0, 100] });
      this.showHelper = ParamConfig.BOOLEAN(DEFAULT.showHelper);
      this.helperSize = ParamConfig.FLOAT(DEFAULT.helperSize, { visibleIf: { showHelper: 1 } });
      this.shadow = ParamConfig.FOLDER();
      this.castShadow = ParamConfig.BOOLEAN(DEFAULT.castShadow);
      this.shadowAutoUpdate = ParamConfig.BOOLEAN(DEFAULT.shadowAutoUpdate, {
        visibleIf: { castShadow: 1 }
      });
      this.shadowUpdateOnNextRender = ParamConfig.BOOLEAN(DEFAULT.shadowUpdateOnNextRender, {
        visibleIf: { castShadow: 1, shadowAutoUpdate: 0 }
      });
      this.shadowRes = ParamConfig.VECTOR2(DEFAULT.shadowRes.toArray(), {
        visibleIf: { castShadow: 1 }
      });
      this.shadowBias = ParamConfig.FLOAT(DEFAULT.shadowBias, {
        visibleIf: { castShadow: 1 },
        range: [-0.01, 0.01],
        rangeLocked: [false, false]
      });
      this.shadowNear = ParamConfig.FLOAT(DEFAULT.shadowNear, {
        visibleIf: { castShadow: 1 },
        range: [0, 100],
        rangeLocked: [true, false]
      });
      this.shadowFar = ParamConfig.FLOAT(DEFAULT.shadowFar, {
        visibleIf: { castShadow: 1 },
        range: [0, 100],
        rangeLocked: [true, false]
      });
      this.shadowRadius = ParamConfig.FLOAT(DEFAULT.shadowRadius, {
        visibleIf: { castShadow: 1 },
        range: [0, 10],
        rangeLocked: [true, false]
      });
      this.volumetric = ParamConfig.FOLDER();
      this.tvolumetric = ParamConfig.BOOLEAN(DEFAULT.tvolumetric);
      this.volAttenuation = ParamConfig.FLOAT(DEFAULT.volAttenuation, {
        range: [0, 10],
        rangeLocked: [true, false]
      });
      this.volAnglePower = ParamConfig.FLOAT(DEFAULT.volAnglePower, {
        range: [0, 20],
        rangeLocked: [true, false]
      });
    }
  };
}
export class SpotLightContainer extends Group {
  constructor(params, nodeName) {
    super();
    this.nodeName = nodeName;
    this.matrixAutoUpdate = false;
    this.params = {
      showHelper: false,
      helperSize: 1,
      tvolumetric: false,
      volAnglePower: 1,
      volAttenuation: 1
    };
    if (params.showHelper != null) {
      this.params.showHelper = params.showHelper;
    }
    if (params.tvolumetric != null) {
      this.params.tvolumetric = params.tvolumetric;
    }
    if (params.volAnglePower != null) {
      this.params.volAnglePower = params.volAnglePower;
    }
    if (params.volAttenuation != null) {
      this.params.volAttenuation = params.volAttenuation;
    }
    this._light = new SpotLight();
    this._target = this._light.target;
    this._light.position.set(0, 0, 1);
    this._light.updateMatrix();
    this._target.updateMatrix();
    this._light.matrixAutoUpdate = false;
    this._target.matrixAutoUpdate = false;
    this.name = `SpotLightContainer_${this.nodeName}`;
    this._light.name = `SpotLight_${this.nodeName}`;
    this._target.name = `SpotLightDefaultTarget_${this.nodeName}`;
    this.add(this._light);
    this.add(this._target);
    this.updateHelper();
  }
  updateParams(params) {
    if (params.showHelper != null) {
      this.params.showHelper = params.showHelper;
    }
    if (params.helperSize != null) {
      this.params.helperSize = params.helperSize;
    }
    if (params.tvolumetric != null) {
      this.params.tvolumetric = params.tvolumetric;
    }
    if (params.volAnglePower != null) {
      this.params.volAnglePower = params.volAnglePower;
    }
    if (params.volAttenuation != null) {
      this.params.volAttenuation = params.volAttenuation;
    }
  }
  light() {
    return this._light;
  }
  copy(source, recursive) {
    const srcLight = source.light();
    this._light.copy(srcLight);
    this.position.copy(source.position);
    this.rotation.copy(source.rotation);
    this.scale.copy(source.scale);
    this.quaternion.copy(source.quaternion);
    this.matrix.copy(source.matrix);
    this.matrixWorld.copy(source.matrixWorld);
    this.updateParams(source.params);
    this.updateHelper();
    if (recursive) {
      this.updateVolumetric();
      this.add(this._light.target);
    }
    return this;
  }
  clone(recursive) {
    const cloned = new SpotLightContainer(this.params, this.nodeName);
    cloned.copy(this);
    return cloned;
  }
  updateHelper() {
    if (isBooleanTrue(this.params.showHelper)) {
      this.__helper__ = this.__helper__ || new CoreSpotLightHelper(this);
      this.add(this.__helper__.object);
      this.__helper__.update({ helperSize: this.params.helperSize });
    } else {
      if (this.__helper__) {
        this.remove(this.__helper__.object);
      }
    }
  }
  updateVolumetric() {
    if (isBooleanTrue(this.params.tvolumetric)) {
      this.__volumetric__ = this.__volumetric__ || new VolumetricSpotLight(this);
      this.__volumetric__.update(this.params);
    } else {
      if (this.__volumetric__) {
        this.__volumetric__.update(this.params);
      }
    }
  }
}
